'use client';

import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import React, { useState } from 'react';
import get from 'lodash/get';
import {
  PlayCircleOutlined,
  PictureOutlined,
  AudioOutlined,
  CloseOutlined,
  DownloadOutlined,
  EyeOutlined,
  LinkOutlined,
  LeftOutlined,
  RightOutlined,
  ReadOutlined,
  AreaChartOutlined,
  FileTextOutlined,
  BarChartOutlined,
} from '@ant-design/icons';
import { BASE_FILE_URL, BASE_URL } from '@/common/constants';
import { useTranslations } from 'next-intl';
import { useParams } from 'next/navigation';
import DetailContent from './DetailContent';

const MultimediaInterface = (props) => {
  const { titlePage, medias, websiteCode } = props;
  const deviceType = props.deviceType === 'mobile' ? 'mobile' : 'tablet';
  const params = useParams();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const { type } = params;

  const [showPopup, setShowPopup] = useState(false);
  const [openNewPage, setOpenNewPage] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState(null);

  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const router = useRouter();
  const t = useTranslations('');

  const currentPage = medias?.page || 1;
  const totalPages = medias?.totalPages ?? 0;

  const handleSubmitUpViewMediaContent = async (id) => {
    try {
      await fetch(`${BASE_URL}/mediaContents/updateViews/${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      });
    } catch {
      // alert(`Có lỗi xảy ra! ${error}`);
    }
  };

  const getMediaType = (item, urlIndex = 0) => {
    if (!item) return 'unknown';

    // Check for specific types first (longforms and infographics)
    if (item.type === 'longforms') {
      return 'longforms';
    }
    if (item.type === 'infographics') {
      return 'infographics';
    }

    // Check if it's a link
    if (item.isLink && item.link) {
      if (item.typeLink === 'youtube' || item.typeLink === 'facebook') {
        return 'video';
      } else if (item.typeLink) {
        const extension = item.link.split('.').pop().toLowerCase().split('?')[0];
        if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv'].includes(extension)) {
          return 'video';
        } else if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(extension)) {
          return 'image';
        } else if (['mp3', 'wav', 'ogg', 'aac', 'flac', 'm4a'].includes(extension)) {
          return 'audio';
        }
        return 'link';
      }
      return 'link';
    }

    // Handle fileUrls array
    if (!item.fileUrls || item.fileUrls.length === 0) return 'unknown';
    const fileUrl = item.fileUrls[urlIndex] || item.fileUrls[0];
    const extension = fileUrl.split('.').pop().toLowerCase();

    if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv'].includes(extension)) {
      return 'video';
    } else if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(extension)) {
      return 'image';
    } else if (['mp3', 'wav', 'ogg', 'aac', 'flac', 'm4a'].includes(extension)) {
      return 'audio';
    }

    return 'unknown';
  };

  function checkTypeImg(type, data, defaultUrl) {
    if (type === 'mobile') {
      return data?.small || defaultUrl;
    }
    if (type === 'tablet') {
      return data?.medium || defaultUrl;
    }
    if (type === 'laptop') {
      return data?.large || defaultUrl;
    }
    return data?.origin || defaultUrl;
  }
  // Get media URL (file or link)
  const getMediaUrl = (item, index = 0) => {
    if (item.isLink && item.link) {
      return item.link;
    }
    if (!item.fileUrls || item.fileUrls.length === 0) return null;
    const dataUrl = `${item.fileUrls[index] || item.fileUrls[0]}`;
    return dataUrl;
  };

  // Get YouTube video ID from URL
  const getYouTubeVideoId = (url) => {
    const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
    const match = url.match(regExp);
    return match && match[7].length === 11 ? match[7] : null;
  };

  // Get YouTube thumbnail
  const getYouTubeThumbnail = (url) => {
    const videoId = getYouTubeVideoId(url);
    return videoId ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg` : null;
  };

  // Render media preview for cards
  const renderCardPreview = (item) => {
    const mediaType = getMediaType(item);
    const mediaUrl = getMediaUrl(item);
    switch (mediaType) {
      case 'longforms':
        return (
          <div
            className={`relative w-full h-56 rounded-t-2xl 
    ${
      item?.avatarUrl
        ? ''
        : 'bg-gradient-to-br from-yellow-400 via-orange-500 to-red-500 group-hover:from-yellow-500 group-hover:via-orange-600 group-hover:to-red-600'
    } 
    flex flex-col items-center justify-center transition-all duration-500`}>
            {/* Layer ảnh hoặc skeleton */}
            <div className="absolute inset-0 rounded-t-2xl overflow-hidden">
              {item?.avatarUrl ? (
                <img
                  src={`${BASE_FILE_URL}/${checkTypeImg(deviceType, item?.sharpAvatar, item?.avatarUrl)}`}
                  alt="img"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="grid grid-cols-6 gap-2 p-4 h-full">
                  {[...Array(24)].map((_, i) => (
                    <div
                      key={i}
                      className="bg-white rounded-sm opacity-60"
                      style={{
                        height: `${Math.random() * 20 + 10}px`,
                        marginTop: `${Math.random() * 10}px`,
                      }}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Optional: Gradient overlay if you still want some color over image */}
            {/* {item?.avatarUrl && (
    <div className="absolute inset-0 bg-gradient-to-br from-yellow-400 via-orange-500 to-red-500 opacity-30 rounded-t-2xl" />
  )} */}

            {/* Nội dung */}
            <div className="relative z-10 text-center">
              <div className="bg-white/20 backdrop-blur-sm rounded-full p-4 mb-4 transform group-hover:scale-110 transition-transform duration-300">
                <ReadOutlined className="text-4xl text-white" />
              </div>
            </div>

            {/* Tag ở góc trái */}
            <div className="absolute top-3 left-3 bg-yellow-600 text-white px-2 py-1 rounded-lg text-xs font-medium">
              <FileTextOutlined className="mr-1" />
              Longform
            </div>

            {/* Tag số lượng files */}
            {item.fileUrls?.length > 1 && (
              <div className="absolute top-3 right-3 bg-orange-600 text-white px-2 py-1 rounded-lg text-xs font-medium">
                {item.fileUrls.length} files
              </div>
            )}
          </div>
        );

      case 'infographics':
        return (
          <div
            className="relative w-full h-56 rounded-t-2xl bg-gradient-to-br from-cyan-400 via-blue-500 to-purple-600 flex flex-col items-center justify-center
           group-hover:from-cyan-500 group-hover:via-blue-600 group-hover:to-purple-700 transition-all duration-500">
            <div className="absolute inset-0 rounded-t-2xl overflow-hidden">
              {item?.avatarUrl ? (
                <img
                  src={`${BASE_FILE_URL}/${checkTypeImg(deviceType, item?.sharpAvatar, item?.avatarUrl)}`}
                  alt="img"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="grid grid-cols-6 gap-2 p-4 h-full">
                  {[...Array(8)].map((_, i) => (
                    <div
                      key={i}
                      className="bg-white rounded-t-lg transition-all duration-1000 ease-in-out"
                      style={{
                        width: '12px',
                        height: `${Math.random() * 80 + 20}%`,
                        animationDelay: `${i * 0.2}s`,
                      }}
                    />
                  ))}
                </div>
              )}
            </div>

            <div className="relative z-10 text-center">
              <div className="bg-white/20 backdrop-blur-sm rounded-full p-4 mb-4 transform group-hover:scale-110 transition-transform duration-300">
                <AreaChartOutlined className="text-4xl text-white" />
              </div>
            </div>

            <div className="absolute top-3 left-3 bg-cyan-600 text-white px-2 py-1 rounded-lg text-xs font-medium">
              <BarChartOutlined className="mr-1" />
              Infographic
            </div>
            {item.fileUrls?.length > 1 && (
              <div className="absolute top-3 right-3 bg-blue-600 text-white px-2 py-1 rounded-lg text-xs font-medium">
                {item.fileUrls.length} files
              </div>
            )}
          </div>
        );

      case 'video':
        if (item.isLink && item.typeLink === 'youtube') {
          const thumbnail = getYouTubeThumbnail(item.link);
          return (
            <div className="relative w-full h-56 rounded-t-2xl overflow-hidden bg-gradient-to-br from-red-600 to-red-800">
              {thumbnail && (
                <img
                  src={thumbnail}
                  alt={item?.name}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent">
                <div className="absolute inset-0 flex items-center justify-center transition-all duration-300">
                  <div className="bg-red-600/80 backdrop-blur-sm rounded-full p-4 transform scale-75 group-hover:scale-100 transition-transform duration-300">
                    <PlayCircleOutlined className="text-4xl !text-white" />
                  </div>
                </div>
                <div className="absolute top-3 left-3 bg-red-600 text-white px-2 py-1 rounded-lg text-xs font-medium">YouTube</div>
              </div>
            </div>
          );
        }

        if (item.isLink && item.typeLink === 'facebook') {
          return (
            <div className="relative w-full h-56 rounded-t-2xl overflow-hidden bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center">
              <div className="text-center">
                <div className="bg-white/20 backdrop-blur-sm rounded-full p-4 mb-4 transform group-hover:scale-110 transition-transform duration-300">
                  <PlayCircleOutlined className="text-4xl text-white" />
                </div>
                <div className="text-white font-medium text-sm opacity-90">Facebook Video</div>
              </div>
              <div className="absolute top-3 left-3 bg-blue-600 text-white px-2 py-1 rounded-lg text-xs font-medium">Facebook</div>
            </div>
          );
        }

        return (
          <div className="relative w-full h-56 rounded-t-2xl overflow-hidden bg-gradient-to-br from-slate-900 to-slate-700">
            <video
              src={`${BASE_FILE_URL}/${mediaUrl}`}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              poster={
                "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='420' height='340'%3E" +
                "%3Crect width='100%25' height='100%25' fill='%23334155'/%3E%3C/svg%3E"
              }
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent">
              <div className="absolute inset-0 flex items-center justify-center transition-all duration-300">
                <div className="bg-white/20 backdrop-blur-sm rounded-full p-4 transform scale-75 group-hover:scale-100 transition-transform duration-300">
                  <PlayCircleOutlined className="text-4xl !text-white" />
                </div>
              </div>
              {item.isLink && (
                <div className="absolute top-3 left-3 bg-purple-600 text-white px-2 py-1 rounded-lg text-xs font-medium">
                  <LinkOutlined className="mr-1" />
                  Link
                </div>
              )}
              {item.fileUrls?.length > 1 && (
                <div className="absolute top-3 right-3 bg-blue-600 text-white px-2 py-1 rounded-lg text-xs font-medium">
                  {item.fileUrls.length} files
                </div>
              )}
            </div>
          </div>
        );

      case 'image':
        return (
          <div className="relative w-full h-56 rounded-t-2xl overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200">
            <img
              src={`${BASE_FILE_URL}/${checkTypeImg(
                deviceType,
                item?.sharpFileUrls?.find((data) => data?.origin === mediaUrl),
                mediaUrl,
              )}`}
              alt={item?.name}
              className="w-full h-full object-cover transition-all duration-500 group-hover:scale-110 group-hover:brightness-110"
              onError={(e) => {
                e.target.src =
                  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='420' height='340'%3E" +
                  "%3Crect width='100%25' height='100%25' fill='%23f1f5f9'/%3E" +
                  "%3Ctext x='50%25' y='50%25' fill='%2364748b' text-anchor='middle' dy='.3em'%3EImage%3C/text%3E%3C/svg%3E";
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300">
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="bg-white/20 backdrop-blur-sm rounded-full p-3 transform scale-75 group-hover:scale-100 transition-transform duration-300">
                  <EyeOutlined className="text-2xl text-white" />
                </div>
              </div>
            </div>
            {item.isLink && (
              <div className="absolute top-3 left-3 bg-green-600 text-white px-2 py-1 rounded-lg text-xs font-medium">
                <LinkOutlined className="mr-1" />
                Link
              </div>
            )}
            {item.fileUrls?.length > 1 && (
              <div className="absolute top-3 right-3 bg-green-600 text-white px-2 py-1 rounded-lg text-xs font-medium">
                {item.fileUrls.length} files
              </div>
            )}
          </div>
        );

      case 'audio':
        return (
          <div className="relative w-full h-56 rounded-t-2xl bg-gradient-to-br from-purple-500 via-pink-500 to-rose-500 flex flex-col items-center justify-center group-hover:from-purple-600 group-hover:via-pink-600 group-hover:to-rose-600 transition-all duration-500">
            <div className="absolute inset-0 opacity-20">
              <div className="flex items-end justify-center h-full space-x-1 px-8">
                {[...Array(20)].map((_, i) => (
                  <div
                    key={i}
                    className="bg-white rounded-full transition-all duration-1000 ease-in-out"
                    style={{
                      width: '4px',
                      height: `${Math.random() * 60 + 20}%`,
                      animationDelay: `${i * 0.1}s`,
                    }}
                  />
                ))}
              </div>
            </div>

            <div className="relative z-10 text-center">
              <div className="bg-white/20 backdrop-blur-sm rounded-full p-4 mb-4 transform group-hover:scale-110 transition-transform duration-300">
                <AudioOutlined className="text-4xl text-white" />
              </div>
              <div className="text-white font-medium text-sm opacity-90">
                {item.fileUrls?.length > 1 ? `Playlist (${item.fileUrls.length} tracks)` : 'Click to play'}
              </div>
            </div>

            {item.isLink && (
              <div className="absolute top-3 left-3 bg-orange-600 text-white px-2 py-1 rounded-lg text-xs font-medium">
                <LinkOutlined className="mr-1" />
                Link
              </div>
            )}
            {item.fileUrls?.length > 1 && (
              <div className="absolute top-3 right-3 bg-purple-600 text-white px-2 py-1 rounded-lg text-xs font-medium">
                {item.fileUrls.length} tracks
              </div>
            )}
          </div>
        );

      default:
        return (
          <div className="relative w-full h-56 rounded-t-2xl bg-gradient-to-br from-gray-300 to-gray-400 flex items-center justify-center">
            <div className="text-center">
              <PlayCircleOutlined className="text-4xl text-gray-600 mb-2" />
              <div className="text-gray-600 text-sm">Unsupported format</div>
            </div>
          </div>
        );
    }
  };

  const [animatePopup, setAnimatePopup] = useState(false);

  const openMediaPopup = (item) => {
    setSelectedMedia(item);

    if (item?.type === 'longforms' || item?.type === 'infographics') {
      // setOpenNewPage(true);
      router.push(`/da-phuong-tien/${item.type}/${item.slug}`);
      return;
    }
    setCurrentMediaIndex(0);
    setShowPopup(true);
    setTimeout(() => setAnimatePopup(true), 10);
  };

  const closePopup = () => {
    setAnimatePopup(false);
    setTimeout(() => setShowPopup(false), 300);
  };

  const handleNextMedia = () => {
    if (!selectedMedia) return;
    const maxIndex = selectedMedia.isLink ? 0 : (selectedMedia.fileUrls?.length || 1) - 1;
    setCurrentMediaIndex((prev) => (prev < maxIndex ? prev + 1 : 0));
  };

  const handlePrevMedia = () => {
    if (!selectedMedia) return;
    const maxIndex = selectedMedia.isLink ? 0 : (selectedMedia.fileUrls?.length || 1) - 1;
    setCurrentMediaIndex((prev) => (prev > 0 ? prev - 1 : maxIndex));
  };

  const handleChangePage = (page) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('page', page);
    router.push(`${pathname}?${newParams.toString()}`);
  };

  const renderPopupContent = () => {
    if (!selectedMedia) return null;

    const mediaType = getMediaType(selectedMedia, currentMediaIndex);
    const mediaUrl = getMediaUrl(selectedMedia, currentMediaIndex);
    const totalFiles = selectedMedia.isLink ? 1 : selectedMedia.fileUrls?.length || 1;

    switch (mediaType) {
      case 'longforms':
        return (
          <div className="bg-white rounded-lg">
            <div className="w-[600px]" style={{ margin: '0px auto' }}>
              <div
                className="text-md content-articles ck ck-content ck-editor__editable 
                              ck-rounded-corners ck-editor__editable_inline ck-blurred !overflow-y-hidden"
                dangerouslySetInnerHTML={{ __html: selectedMedia?.title ?? '' }}
              />
            </div>
            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-6 text-gray-800">
              <div className="text-left space-y-4">
                <p className="text-lg leading-relaxed text-gray-800">{selectedMedia?.content || 'No content available'}</p>
                {selectedMedia.fileUrls?.length > 0 && (
                  <div className="mt-6">
                    <h4 className="font-semibold mb-4">Attached Files:</h4>
                    <div className="grid grid-cols-1 gap-3">
                      {selectedMedia.fileUrls.map((fileUrl, index) => (
                        <a
                          key={index}
                          href={`${BASE_FILE_URL}/${fileUrl}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center space-x-3 bg-white/20 hover:bg-white/30 rounded-lg p-3 transition-all duration-300">
                          <FileTextOutlined className="text-xl" />
                          <span className="flex-1 text-left truncate">File {index + 1}</span>
                          <EyeOutlined className="text-lg" />
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      case 'infographics':
        return (
          <div className="bg-gradient-to-br from-cyan-500 via-blue-500 to-purple-600 p-8 rounded-2xl text-center">
            <AreaChartOutlined className="text-6xl text-white mb-6" />
            <h3 className="text-2xl font-bold text-white mb-6">Infographic Content</h3>
            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-6 text-white">
              <div className="text-left space-y-4">
                <p className="text-lg leading-relaxed">{selectedMedia?.content || 'No content available'}</p>
                {selectedMedia.fileUrls?.length > 0 && (
                  <div className="mt-6">
                    <h4 className="font-semibold mb-4">Infographic Files:</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {selectedMedia.fileUrls.map((fileUrl, index) => (
                        <a
                          key={index}
                          href={`${BASE_FILE_URL}/${fileUrl}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center space-x-3 bg-white/20 hover:bg-white/30 rounded-lg p-3 transition-all duration-300">
                          <BarChartOutlined className="text-xl" />
                          <span className="flex-1 text-left truncate">Infographic {index + 1}</span>
                          <EyeOutlined className="text-lg" />
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      case 'video':
        if (selectedMedia.isLink && selectedMedia.typeLink === 'youtube') {
          const videoId = getYouTubeVideoId(selectedMedia.link);
          return (
            <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
              <iframe
                src={`https://www.youtube.com/embed/${videoId}?autoplay=1`}
                className="absolute top-0 left-0 w-full h-full rounded-lg"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          );
        }

        if (selectedMedia.isLink && selectedMedia.typeLink === 'facebook') {
          return (
            <div className="bg-gradient-to-br from-blue-600 to-blue-800 p-8 rounded-2xl text-center">
              <PlayCircleOutlined className="text-6xl text-white mb-6" />
              <h3 className="text-2xl font-bold text-white mb-6">Facebook Video</h3>
              <a
                href={selectedMedia.link}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-2 bg-white/20 hover:bg-white/30 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300">
                <span>Xem trên Facebook</span>
                <LinkOutlined />
              </a>
            </div>
          );
        }

        return (
          <div className="relative">
            <video src={`${BASE_FILE_URL}/${mediaUrl}`} controls autoPlay className="max-w-full max-h-[70vh] rounded-lg" />
            {totalFiles > 1 && (
              <div className="absolute bottom-4 left-4 bg-black/50 text-white px-3 py-1 rounded-lg text-sm">
                File {currentMediaIndex + 1} of {totalFiles}
              </div>
            )}
          </div>
        );

      case 'image':
        return (
          <div className="relative">
            <img
              src={`${BASE_FILE_URL}/${checkTypeImg(
                deviceType,
                selectedMedia?.sharpFileUrls?.find((data) => data?.origin === mediaUrl),
                mediaUrl,
              )}`}
              alt={selectedMedia?.name}
              className="max-w-full max-h-[80vh] rounded-lg"
            />
            {totalFiles > 1 && (
              <div className="absolute bottom-4 left-4 bg-black/50 text-white px-3 py-1 rounded-lg text-sm">
                File {currentMediaIndex + 1} of {totalFiles}
              </div>
            )}
          </div>
        );

      case 'audio':
        return (
          <div className="bg-gradient-to-br from-purple-600 to-pink-600 p-8 rounded-2xl text-center">
            <AudioOutlined className="text-6xl text-white mb-6" />
            <h3 className="text-2xl font-bold text-white mb-6">
              {selectedMedia?.name} {totalFiles > 1 && `(${currentMediaIndex + 1}/${totalFiles})`}
            </h3>
            <audio src={`${BASE_FILE_URL}/${mediaUrl}`} controls autoPlay className="w-full" onEnded={totalFiles > 1 ? handleNextMedia : null} />
            {totalFiles > 1 && (
              <div className="flex justify-center mt-6 space-x-4">
                <button onClick={handlePrevMedia} className="bg-white/20 hover:bg-white/30 text-white rounded-full p-3 transition-all duration-300">
                  <LeftOutlined />
                </button>
                <button onClick={handleNextMedia} className="bg-white/20 hover:bg-white/30 text-white rounded-full p-3 transition-all duration-300">
                  <RightOutlined />
                </button>
              </div>
            )}
          </div>
        );

      default:
        return (
          <div className="text-white text-center p-8">
            <p>Không thể hiển thị media này</p>
          </div>
        );
    }
  };

  const tabs = [
    { key: 'video', label: 'Video', icon: <PlayCircleOutlined />, gradient: 'from-blue-500 to-purple-600' },
    { key: 'image', label: t('media.image'), icon: <PictureOutlined />, gradient: 'from-green-500 to-teal-600' },
    { key: 'audio', label: t('media.sound'), icon: <AudioOutlined />, gradient: 'from-purple-500 to-pink-600' },
    { key: 'longforms', label: 'Longforms', icon: <ReadOutlined />, gradient: 'from-yellow-500 to-orange-500' },
    { key: 'infographics', label: 'Infographics', icon: <AreaChartOutlined />, gradient: 'from-cyan-500 to-lime-500' },
  ];

  return (
    <div className={`min-h-screen`}>
      <section className="bg-gradient-to-r from-red-400 via-orange-500 to-orange-600 shadow-2xl container mx-auto px-2 md:px-4">
        <div className="container mx-auto px-4 sm:px-4 py-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6">
            <div>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white tracking-wide mb-2">
                {titlePage || t('media.multimedia_gallery')}
              </h1>
            </div>

            {/* Navigation Tabs */}
            <section className="flex space-x-2 bg-white/10 rounded-2xl p-2 backdrop-blur-lg border border-white/20">
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => {
                    router.push(`${websiteCode ? `/${websiteCode}` : ''}/da-phuong-tien/${tab.key}`);
                  }}
                  className={`flex items-center space-x-2 px-4 sm:px-6 py-3 rounded-xl font-medium transition-all duration-300 ${
                    type === tab.key
                      ? `bg-gradient-to-r ${tab.gradient} text-white shadow-lg transform scale-105`
                      : 'text-white/80 hover:text-white hover:bg-white/10 hover:scale-105'
                  }`}>
                  <span className="text-lg">{tab.icon}</span>
                  <span className="hidden md:inline text-sm font-semibold">{tab.label}</span>
                </button>
              ))}
            </section>
          </div>
        </div>
      </section>

      {/* Main Content */}
      {openNewPage ? (
        <DetailContent selectedMedia={selectedMedia} setOpenNewPage={setOpenNewPage} />
      ) : (
        <section className="container  bg-white mx-auto px-4 sm:px-6 py-8">
          {get(medias, 'results', []).length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="text-6xl text-gray-300 mb-6">
                {type === 'video' ? <PlayCircleOutlined /> : type === 'audio' ? <AudioOutlined /> : <PictureOutlined />}
              </div>
              <p className="text-xl text-gray-500 mb-2">
                {t('media.statusTale')} {type === 'video' ? 'video' : type === 'audio' ? 'audio' : 'hình ảnh'}
              </p>
              <p className="text-gray-400">{t('media.tryIt')}</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 mb-8">
                {get(medias, 'results', []).map((item, index) => (
                  <div
                    key={item.id}
                    className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 
                  overflow-hidden border border-gray-100 hover:border-gray-300 cursor-pointer transform hover:-translate-y-2"
                    onClick={() => {
                      openMediaPopup(item);
                      handleSubmitUpViewMediaContent(item?.id);
                    }}>
                    {/* Media Preview */}
                    {renderCardPreview(item)}

                    {/* Content */}
                    <div className="p-6">
                      {/* Media Type Badge */}
                      <div className="flex items-center justify-between mb-3">
                        <div
                          className={`inline-flex items-center space-x-1 px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r ${
                            getMediaType(item) === 'audio'
                              ? 'from-purple-100 to-pink-100 text-purple-700'
                              : getMediaType(item) === 'image'
                              ? 'from-green-100 to-teal-100 text-green-700'
                              : 'from-blue-100 to-purple-100 text-blue-700'
                          }`}>
                          {(() => {
                            const type = getMediaType(item);
                            if (type === 'audio') {
                              return (
                                <>
                                  <AudioOutlined className="text-xs" />
                                  <span>Audio</span>
                                </>
                              );
                            } else if (type === 'image') {
                              return (
                                <>
                                  <PictureOutlined className="text-xs" />
                                  <span>Image</span>
                                </>
                              );
                            } else if (type === 'video') {
                              return (
                                <>
                                  <PlayCircleOutlined className="text-xs" />
                                  <span>Video</span>
                                </>
                              );
                            }
                          })()}
                        </div>
                        <span className="text-xs text-gray-400 font-medium">#{index + 1}</span>
                      </div>

                      <h3 className="font-bold text-gray-900 text-lg mb-3 line-clamp-2 group-hover:text-blue-600 transition-colors leading-tight">
                        {item?.name || 'Untitled Media'}
                      </h3>

                      <p className="text-gray-600 text-sm line-clamp-3 mb-4 leading-relaxed">{item?.content || ''}</p>

                      {/* Actions */}
                      <div className="flex items-center justify-between">
                        <button
                          className="flex items-center space-x-2 text-blue-600 
                      hover:text-blue-700 font-semibold text-sm group-hover:scale-105 transition-all duration-300">
                          {getMediaType(item) === 'audio' ? (
                            <AudioOutlined className="text-base" />
                          ) : getMediaType(item) === 'image' ? (
                            <EyeOutlined className="text-base" />
                          ) : (
                            <PlayCircleOutlined className="text-base" />
                          )}
                          <span>{t('media.seeNow')}</span>
                        </button>
                        {item.isLink && (
                          <div className="flex items-center space-x-1 text-xs text-gray-500">
                            <LinkOutlined />
                            <span>Link</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {totalPages > 1 && (
                <div className="mt-4 flex flex-wrap justify-center space-x-2 w-full">
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter((page) => {
                      return (
                        page <= 3 || // 3 đầu
                        page > totalPages - 3 || // 3 cuối
                        (page >= currentPage - 1 && page <= currentPage + 1) // xung quanh trang hiện tại
                      );
                    })
                    .reduce((acc, page, i, arr) => {
                      if (i > 0 && page - arr[i - 1] > 1) {
                        acc.push('ellipsis-' + i); // Dùng key khác biệt
                      }
                      acc.push(page);
                      return acc;
                    }, [])
                    .map((item) => {
                      if (typeof item === 'string' && item.startsWith('ellipsis')) {
                        const middle = Math.floor(totalPages / 2);
                        return (
                          <button
                            key={item}
                            onClick={() => handleChangePage(middle)}
                            className="px-3 py-1 border rounded bg-white text-gray-500 border-gray-300">
                            ...
                          </button>
                        );
                      } else {
                        return (
                          <button
                            key={item}
                            onClick={() => handleChangePage(item)}
                            className={`px-3 py-1 border rounded ${
                              currentPage === item ? 'bg-red-600 text-white' : 'bg-white text-gray-700 border-gray-300'
                            }`}>
                            {item}
                          </button>
                        );
                      }
                    })}
                </div>
              )}
            </>
          )}
        </section>
      )}

      {/* Media Popup */}
      {showPopup && (
        <div
          className={`fixed inset-0 z-50 p-4 flex items-center justify-center
    bg-black/90 backdrop-blur-sm transition-opacity duration-300 
    ${animatePopup ? 'opacity-100' : 'opacity-0'}`}>
          <div
            className={`relative max-w-6xl w-full transform transition-all duration-300
      ${animatePopup ? 'scale-100 translate-y-0 opacity-100' : 'scale-95 translate-y-4 opacity-0'}`}>
            {/* Close button */}
            <button
              onClick={closePopup}
              className="absolute -top-6 right-[-22px] w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex cursor-pointer
        items-center justify-center text-white backdrop-blur-sm transition-all duration-300 hover:scale-110 hover:rotate-90 z-10">
              <CloseOutlined className="text-xl" />
            </button>

            {/* Navigation arrows for multiple files (except audio which has its own controls) */}
            {selectedMedia?.fileUrls?.length > 1 && getMediaType(selectedMedia) !== 'audio' && (
              <>
                <button
                  onClick={handlePrevMedia}
                  className="fixed left-[-8px] top-1/2 -translate-y-1/2 
        w-10 h-10 sm:w-12 sm:h-12 bg-white/10 hover:bg-white/20 rounded-full 
        flex items-center justify-center text-white backdrop-blur-sm 
        transition-all duration-300 hover:scale-110 z-50 cursor-pointer">
                  <LeftOutlined className="text-lg sm:text-xl" />
                </button>
                <button
                  onClick={handleNextMedia}
                  className="fixed right-[-8px] top-1/2 -translate-y-1/2 
        w-10 h-10 sm:w-12 sm:h-12 bg-white/10 hover:bg-white/20 rounded-full 
        flex items-center justify-center text-white backdrop-blur-sm 
        transition-all duration-300 hover:scale-110 z-50 cursor-pointer">
                  <RightOutlined className="text-lg sm:text-xl" />
                </button>
              </>
            )}

            {/* Media content */}
            <div className="bg-black/30 backdrop-blur-lg rounded-3xl p-6 border border-white/10 shadow-2xl overflow-y-auto max-h-[90vh]">
              <div className="flex flex-col">
                <h3 className="text-2xl sm:text-3xl font-bold text-white mb-2">{selectedMedia?.name}</h3>
                <p className="text-lg text-gray-300 mb-6 leading-relaxed ">{selectedMedia?.content}</p>

                <div className="mb-6">{renderPopupContent()}</div>

                {/* Action buttons */}
                {selectedMedia?.type !== 'longforms' && (
                  <div className="flex flex-wrap gap-4">
                    {!selectedMedia?.isLink && (
                      <a
                        href={`${BASE_FILE_URL}/${getMediaUrl(selectedMedia, currentMediaIndex)}`}
                        download
                        className="flex items-center space-x-2 bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 
                  hover:to-red-700 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 
                  hover:scale-105 shadow-lg">
                        <DownloadOutlined />
                        <span>{t('media.download')}</span>
                      </a>
                    )}

                    {selectedMedia?.isLink && (
                      <a
                        href={selectedMedia.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center space-x-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 
                  hover:to-purple-700 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 
                  hover:scale-105 shadow-lg">
                        <LinkOutlined />
                        <span>Mở liên kết</span>
                      </a>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MultimediaInterface;
